import {
	Map as MapTilerMap,
	Marker,
	MapStyle,
	config as mapTilerConfig,
} from '@maptiler/sdk';
import { __ } from '@wordpress/i18n';
import { apiFetch } from './api';

// Canary Islands, used when no coordinates are set yet.
const DEFAULT_CENTER = [ -15.6, 28.3 ];
const DEFAULT_ZOOM = 7;
const MARKER_ZOOM = 14;

const STATUS_MESSAGES = {
	not_found: __( 'Address not found.', 'anacrm' ),
	no_address: __( 'Fill in the address first.', 'anacrm' ),
	error: __( 'Geocoding failed.', 'anacrm' ),
};

function findFieldWrapper( form, name ) {
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

function selectedTermIds( wrapper, fieldName ) {
	if ( ! wrapper ) {
		return [];
	}
	return [
		...wrapper.querySelectorAll(
			`input[type="hidden"][name^="pods_field_${ fieldName }"]`
		),
	]
		.map( ( el ) => parseInt( el.value ) )
		.filter( ( id ) => ! isNaN( id ) );
}

/**
 * Initializes the draggable-marker map + "Geocode" button for a space/org
 * location picker. Inserts the map right after the address field and drives
 * the (hidden) lat/lng Pods fields from it. No-ops if the API key, the
 * address field, or the lat/lng fields aren't present.
 *
 * @param {HTMLFormElement} form          The Pods front-end form element.
 * @param {Object}          params
 * @param {string}          params.prefix Field prefix, e.g. '_sp_' or '_or_'.
 */
export function initLocationPicker( form, { prefix } ) {
	const apiKey = window.anacrm?.mapTilerApiKey;
	if ( ! apiKey ) {
		return;
	}

	const addressWrapper = findFieldWrapper( form, `${ prefix }address` );
	const latWrapper = findFieldWrapper( form, `${ prefix }lat` );
	const lngWrapper = findFieldWrapper( form, `${ prefix }lng` );

	if ( ! addressWrapper || ! latWrapper || ! lngWrapper ) {
		return;
	}

	if ( addressWrapper.dataset.anacrmLocationPicker ) {
		return;
	}
	addressWrapper.dataset.anacrmLocationPicker = '1';

	latWrapper.style.display = 'none';
	lngWrapper.style.display = 'none';

	// Pods renders even plain text fields asynchronously: the server sends a
	// loading placeholder, and Pods' own JS swaps in the real <input> after
	// mount. Wait for that to happen before reading/driving their values.
	function whenInputsReady( callback ) {
		const latInput = latWrapper.querySelector( 'input' );
		const lngInput = lngWrapper.querySelector( 'input' );
		if ( latInput && lngInput ) {
			callback( latInput, lngInput );
			return;
		}

		const observer = new MutationObserver( () => {
			const li = latWrapper.querySelector( 'input' );
			const li2 = lngWrapper.querySelector( 'input' );
			if ( li && li2 ) {
				observer.disconnect();
				callback( li, li2 );
			}
		} );
		observer.observe( latWrapper, { childList: true, subtree: true } );
		observer.observe( lngWrapper, { childList: true, subtree: true } );
	}

	whenInputsReady( ( latInput, lngInput ) => {
		mountPicker( { form, prefix, addressWrapper, latInput, lngInput } );
	} );
}

function mountPicker( { form, prefix, addressWrapper, latInput, lngInput } ) {
	mapTilerConfig.apiKey = window.anacrm.mapTilerApiKey;

	const picker = document.createElement( 'div' );
	picker.className = 'mb-4';

	const mapEl = document.createElement( 'div' );
	mapEl.className = 'w-full h-[300px] border-2 border-black';
	picker.appendChild( mapEl );

	const controls = document.createElement( 'div' );
	controls.className = 'flex flex-wrap items-center gap-3 mt-2';
	picker.appendChild( controls );

	const geocodeBtn = document.createElement( 'button' );
	geocodeBtn.type = 'button';
	geocodeBtn.className =
		'px-3 py-2 text-sm border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold';
	geocodeBtn.textContent = __( 'Geocode from address', 'anacrm' );
	controls.appendChild( geocodeBtn );

	const status = document.createElement( 'span' );
	status.className = 'text-xs';
	controls.appendChild( status );

	const hint = document.createElement( 'p' );
	hint.className = 'text-xs text-gray mt-1';
	hint.textContent = __(
		'Click or drag the marker on the map to set the exact location, or use the geocode button to look it up from the address.',
		'anacrm'
	);
	picker.appendChild( hint );

	addressWrapper.insertAdjacentElement( 'afterend', picker );

	const initialLat = parseFloat( latInput.value );
	const initialLng = parseFloat( lngInput.value );
	const hasInitialCoords = ! isNaN( initialLat ) && ! isNaN( initialLng );

	const map = new MapTilerMap( {
		container: mapEl,
		style: MapStyle.STREETS,
		center: hasInitialCoords ? [ initialLng, initialLat ] : DEFAULT_CENTER,
		zoom: hasInitialCoords ? MARKER_ZOOM : DEFAULT_ZOOM,
	} );

	// The picker lives inside a collapsed accordion <details> section, so the
	// map is initialized with a zero-size container; maplibre-gl doesn't
	// auto-resize on its own, so force a resize once the section opens.
	const detailsEl = picker.closest( 'details' );
	if ( detailsEl ) {
		detailsEl.addEventListener( 'toggle', () => {
			if ( detailsEl.open ) {
				map.resize();
			}
		} );
	}

	let marker = null;

	function setStatus( text, isError ) {
		status.textContent = text;
		status.className = `text-xs ${ isError ? 'text-red' : '' }`;
	}

	function setCoords( lat, lng, { fly } = {} ) {
		latInput.value = lat;
		lngInput.value = lng;
		latInput.dispatchEvent( new Event( 'input', { bubbles: true } ) );
		lngInput.dispatchEvent( new Event( 'input', { bubbles: true } ) );

		if ( marker ) {
			marker.setLngLat( [ lng, lat ] );
		} else {
			marker = new Marker( { draggable: true } )
				.setLngLat( [ lng, lat ] )
				.addTo( map );
			marker.on( 'dragend', () => {
				const lngLat = marker.getLngLat();
				setCoords( lngLat.lat, lngLat.lng );
			} );
		}

		if ( fly ) {
			map.flyTo( { center: [ lng, lat ], zoom: MARKER_ZOOM } );
		}
	}

	if ( hasInitialCoords ) {
		setCoords( initialLat, initialLng );
	}

	map.on( 'click', ( e ) => {
		setStatus( '' );
		setCoords( e.lngLat.lat, e.lngLat.lng );
	} );

	geocodeBtn.addEventListener( 'click', async () => {
		const addressField = addressWrapper.querySelector( 'textarea' );
		const address = addressField ? addressField.value.trim() : '';

		const cityWrapper = findFieldWrapper( form, `${ prefix }city` );
		const regionWrapper = findFieldWrapper( form, `${ prefix }region` );
		const territoryWrapper = findFieldWrapper(
			form,
			`${ prefix }territory`
		);
		const termIds = [
			...selectedTermIds( cityWrapper, `${ prefix }city` ),
			...selectedTermIds( regionWrapper, `${ prefix }region` ),
			...selectedTermIds( territoryWrapper, `${ prefix }territory` ),
		];

		geocodeBtn.disabled = true;
		setStatus( __( 'Geocoding…', 'anacrm' ) );

		try {
			const result = await apiFetch( '/anacrm/v1/geocode/lookup', {
				method: 'POST',
				body: JSON.stringify( { address, termIds } ),
			} );

			if ( result.status === 'geocoded' ) {
				setStatus( '' );
				setCoords( parseFloat( result.lat ), parseFloat( result.lng ), {
					fly: true,
				} );
			} else {
				setStatus(
					STATUS_MESSAGES[ result.status ] ?? STATUS_MESSAGES.error,
					true
				);
			}
		} catch ( err ) {
			setStatus( err.message || STATUS_MESSAGES.error, true );
		} finally {
			geocodeBtn.disabled = false;
		}
	} );
}
